'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface AddClientFormData {
  clientName: string;
  industry: string;
  contactName: string;
  contactEmail: string;
  website: string;
  monthlyBudget: string;
  notes: string;
}

const industries = [
  'Food & Beverage', 'Technology', 'Health & Wellness', 'Home & Décor',
  'Apparel & Fashion', 'Fitness & Sports', 'Renewable Energy', 'Pet Products',
  'Financial Services', 'Real Estate', 'Education', 'Travel & Hospitality',
  'Automotive', 'Beauty & Cosmetics', 'Other',
];

export default function AddClientModal({ open, onClose }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<AddClientFormData>();

  const onSubmit = async (data: AddClientFormData) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/clients/create
    await new Promise((r) => setTimeout(r, 1300));
    toast.success(`${data.clientName} added as a new client — connect their social pages to get started`);
    setIsSubmitting(false);
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Add New Client" subtitle="Onboard a new client account to your agency workspace" size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-base">Client / Business Name</label>
              <input
                type="text"
                placeholder="Apex Retail Co."
                className={`input-base ${errors.clientName ? 'border-negative' : ''}`}
                {...register('clientName', { required: 'Client name is required' })}
              />
              {errors.clientName && <p className="error-text">{errors.clientName.message}</p>}
            </div>
            <div>
              <label className="label-base">Industry</label>
              <select className="input-base" {...register('industry', { required: true })}>
                <option value="">Select industry…</option>
                {industries.map((ind) => <option key={`ind-${ind}`} value={ind}>{ind}</option>)}
              </select>
            </div>
            <div>
              <label className="label-base">Primary Contact Name</label>
              <input
                type="text"
                placeholder="Jordan Smith"
                className="input-base"
                {...register('contactName', { required: true })}
              />
            </div>
            <div>
              <label className="label-base">Contact Email</label>
              <input
                type="email"
                placeholder="jordan@apexretail.com"
                className={`input-base ${errors.contactEmail ? 'border-negative' : ''}`}
                {...register('contactEmail', {
                  required: 'Contact email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.contactEmail && <p className="error-text">{errors.contactEmail.message}</p>}
            </div>
            <div>
              <label className="label-base">Website</label>
              <input
                type="url"
                placeholder="https://apexretail.com"
                className="input-base"
                {...register('website')}
              />
            </div>
            <div>
              <label className="label-base">Monthly Ad Budget (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  type="number"
                  placeholder="5000"
                  className="input-base pl-7"
                  {...register('monthlyBudget')}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="label-base">Onboarding Notes</label>
            <p className="helper-text">Internal notes about this client — goals, preferences, restrictions</p>
            <textarea
              rows={3}
              placeholder="e.g. Focus on Instagram for brand awareness. No competitor brand mentions. Monthly reporting on the 1st."
              className="input-base resize-none mt-1"
              {...register('notes')}
            />
          </div>

          <div className="p-3 rounded-xl bg-info/5 border border-info/20">
            <p className="text-xs font-semibold text-info mb-1">Next step after adding</p>
            <p className="text-xs text-muted-foreground">
              You'll be prompted to connect the client's Facebook Page and Instagram account so you can start publishing content and running ads.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Adding client…
              </span>
            ) : (
              'Add Client'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}